import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  GridList,
  GridListTile,
  IconButton,
  Link,
  InputAdornment,
  Switch,
  TextField,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpenOutlined';
import LockClosedIcon from '@material-ui/icons/LockOutlined';
import {makeStyles} from '@material-ui/styles';
import 'graphiql/graphiql.css';
import {useSnackbar} from 'notistack';
import React, {ChangeEvent, Fragment, useCallback, useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router';
import useReactRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import {checkPermission, checkRole as checkMyRole} from '../../../components/Can/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {
  Resource,
  ResourceBank,
  ResourceOption,
  ResourceOptionAction,
  ResourceType,
  ResourceTypes
} from '../../../graphql/resources.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {formatDate, formatGraphQlDate} from '../../../utils/formatters';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers'
import getResource from '../getResource.api';
import {getBlankResourceOption} from '../ResourceOptionEdit/ResourceOptionEdit';
import ResourcesTree from '../ResourcesTree/ResourcesTree';
import cloneResource from './cloneResource.api';
import createResource, {CreateResourceVars} from './createResource.api';
import deleteResource from './deleteResource.api';
import availableRoomBanks from './getAvailableRoomBanks.api';
import updateResource, {UpdateResourceVars} from './updateResource.api';
import uploadFile, {UploadFileVars} from '../../../graphql/uploadFile.api';
import {WcvType} from '../../../graphql/logs.datatypes';
import {getHost} from '../../../graphql/GraphQlProvider';
import {TourRoleTypes} from '../../../graphql/groups.datatypes';
import MediumEditorField from '../../../components/MediumEditor/MediumEditorField';
import ConfirmModal from '../../../components/ConfirmModal';
import updateResourceOptionCosts from './updateResourceOptionCosts.api';
import SendEmailToGroup from '../../../components/SendEmailToGroup';
import PaymentScheduleDetailsModal from '../../PaymentSchedule/PaymentScheduleDetailsModal';
import {DocumentDetailsContent, getBlankDocument} from '../../Documents/DocumentDetails/DocumentDetails';
import {DocumentListContent} from '../../Documents/DocumentsList/DocumentsList';
import {getMe} from '../../../graphql/auth';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';
import {T} from 'react-polyglot-hooks';
import ChangeResourceParentModal from '../ChangeResourceParentModal';
import fixRoomResourceCapacity from './fixRoomResourceCapacity.api';
import getEmails from "../../EmailManagement/getEmails.api";
import getGlobalCreditCardFee from '../../CreditCardFee/getGlobalCreditCardFee.api';
import {undefinedFieldMessage} from "graphql/validation/rules/FieldsOnCorrectType";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  container: {
    //marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  dateWrapper: {
    flexWrap: 'wrap',

    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap'
    }
  },
  resourceCardHeader: {
    flexWrap: 'wrap',

  },
  buttonWrapper: {
    [theme.breakpoints.down('md')]: {
      flex: 'auto',
    marginTop: 10,
    marginRight: 0,
    flexWrap: 'wrap',

    }
  },
  saveButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
  cloneButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[400],
    '&:hover': {
      backgroundColor: colors.green[700]
    }
  },
  card: {},
  noPadding: {
    padding: '0 !important',
  },
  gridListContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  gridList: {
    flexWrap: 'nowrap',
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: 'translateZ(0)',
    width: '100%',
  },
}));

export const getBlankResource = (parentId: number | undefined = undefined) => ({
  __typename: 'Resource',
  id: -1,
  internal_name: '',
  display_name: '',
  description: '',
  t_and_c_url: '',
  netsuite_tour_type: '',
  disabled: false,
  parent_resource_id: parentId,
  resource_type: ResourceType.GENERAL,
  has_price: false,
  has_dates: false,
  consumable: false,
  allow_multiple: false,
  tour_roles: [],
  guest_capacity: 0,
});

export const ResourceEditPure = (props: {
  resource: Resource,
  onResourceView?: (resource: Resource) => void,
  onResourceBankView?: (resourceBank: ResourceBank) => void,
  onResourceOptionView?: (resourceOption: ResourceOption) => void,
  onResourceCreated?: (resource: Resource) => void,
  onResourceDeleted?: (resourceId: number) => void,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const {history}: RouteComponentProps = useReactRouter();
  const isNewResource = props.resource.id === -1;
  const {enqueueSnackbar} = useSnackbar();
  const [sendCreateResource, createData] = createResource();
  const [sendUpdateResource, updateData] = updateResource();
  const [sendCloneResource, cloneData] = cloneResource();
  const [sendUpdateResourceOptionCosts, updateOptionsData] = updateResourceOptionCosts();
  const [sendFixRoomResourceCapacity, fixRoomResourceCapacityData] = fixRoomResourceCapacity();


  const [openDelete, setOpenDelete] = React.useState(false);
  const [openUpdateOptions, setOpenUpdateOptions] = React.useState(false);
  const [sendDeleteResource, deleteData] = deleteResource();
  const [sendUploadFile, uploadFileData] = uploadFile();
  const resourceData = getResource(props.resource.id);
  const roomBankData = availableRoomBanks(props.resource.id);
  const loadingData = anyLoading(createData, updateData, cloneData, deleteData, roomBankData, uploadFileData, updateOptionsData, fixRoomResourceCapacityData);
  const canLock = checkPermission('resource.lock');
  const [hasActiveOptions, setHasActiveOptions] = useState(false);

  // Separate state for credit card fee toggle to avoid conflicts
  const [creditCardFeeEnabled, setCreditCardFeeEnabled] = useState(!!props.resource.credit_card_fee);

  const meData = getMe();
  const [selectedDocument, setSelectedDocument] = useState();

  const isSuperAdmin = checkMyRole('super-admin');

  const emailData = getEmails();
  const emails = emailData && emailData.data && emailData.data.emails || [];
  const globalCreditCardFee = getGlobalCreditCardFee();

  const checkExistingOptionValues = () => {
    let optionValueExists = false;
    if (props.resource.options && props.resource.options.length > 0) {
      props.resource.options.map(option => {
        if (option.groupResources && option.groupResources.length > 0) {
          option.groupResources.map(groupResource => {
            switch (option.action_type) {
              case ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY:
              case ResourceOptionAction.TRAVELLER_RESOURCE_AVAILABLE:
                if (groupResource.roomMemberOptions && groupResource.roomMemberOptions.length > 0) {
                  groupResource.roomMemberOptions.map(roomMemberOption => {
                    if (roomMemberOption.unit_count > 0) optionValueExists = true;
                  });
                }
                break;
              case ResourceOptionAction.ROOM_RESOURCE_MANDATORY:
              case ResourceOptionAction.ROOM_RESOURCE_AVAILABLE:
                if (groupResource.roomOptions && groupResource.roomOptions.length > 0) {
                  groupResource.roomOptions.map(roomOption => {
                    if (roomOption.unit_count > 0) optionValueExists = true;
                  });
                }
                break;
            }
          })
        }
      });
    }

    return optionValueExists;
  };

  //1. Set form and error state
  const [formState, setFormState] = useState<Resource>({
    ...props.resource,
    t_and_c_url: props.resource.t_and_c_url ? props.resource.t_and_c_url : '',
    comms_email_address: props.resource.comms_email_address ? props.resource.comms_email_address : '',
    invoice_document_id: props.resource.invoice_document_id ? props.resource.invoice_document_id : -1,
    booking_tool_edit_cutoff: props.resource.booking_tool_edit_cutoff ? props.resource.booking_tool_edit_cutoff : '',
    credit_card_fee: props.resource.credit_card_fee ? props.resource.credit_card_fee : undefined,
  });
  useEffect(() => {
    setFormState({
      ...props.resource,
      t_and_c_url: props.resource.t_and_c_url ? props.resource.t_and_c_url : '',
      comms_email_address: props.resource.comms_email_address ? props.resource.comms_email_address : '',
      invoice_document_id: props.resource.invoice_document_id ? props.resource.invoice_document_id : -1,
      booking_tool_edit_cutoff: props.resource.booking_tool_edit_cutoff ? props.resource.booking_tool_edit_cutoff : '',
        credit_card_fee: props.resource.credit_card_fee ? props.resource.credit_card_fee : undefined,
    });
    setCreditCardFeeEnabled(!!props.resource.credit_card_fee);
    setHasActiveOptions(checkExistingOptionValues());
    setUpdatedBanks([]);
    setBanksValueUpdated(false);

    }, [props.resource.id]); // Only reset when resource ID changes, not on any resource update

  // Initialize credit card fee state based on existing resource data (runs only when resource changes)
  useEffect(() => {
    const isTour = props.resource.resource_type === ResourceType.TOUR;
    const resourceHasFee = !!props.resource.credit_card_fee;

    if (!resourceHasFee && !globalCreditCardFee.loading && globalCreditCardFee.data && globalCreditCardFee.data.globalCreditCardFee && globalCreditCardFee.data.globalCreditCardFee.fee && globalCreditCardFee.data.globalCreditCardFee.fee !== '0' && isTour) {
      // New Tour resource: auto-enable CC fee based on global settings
      const globalFee = globalCreditCardFee.data.globalCreditCardFee.fee;
      setFormState(prevState => ({
        ...prevState,
        credit_card_fee: {
          fee: globalFee,
          type: WcvType.RESOURCE,
          type_id: props.resource.id
        }
      }));
    }
  }, [props.resource.id, props.resource.credit_card_fee]) // Only run when resource changes, not form state

  const [errors, setErrors] = useState({
    isValid: false,
    errors: {
      internal_name: false,
      display_name: false,
      description: false,
      available_from: false,
      available_to: false,
      capacity: false,
      count: false,
      warning_count: false,
      allow_manual_overbooking: false,
      unit_price: false,
      show_on_invoice: false,
      disabled: false,
      locked: false,
    }
  });

  //2. Setup helper functions for form
  const handleFieldChange = getHandleFieldChange(setFormState);
  const hasError = getHasError(errors);
  const [updatedBanks, setUpdatedBanks] = useState<number[]>([]);
  const [banksValueUpdated, setBanksValueUpdated] = useState(false);

  const handleBankChange = (key: string, value: any, index: number) => {
    setFormState((oldState: any) => {
      const resourceBanks = [...oldState.resource_banks];
      resourceBanks[index] = {...resourceBanks[index], [key]: Number(value)};
      return {...oldState, resource_banks: resourceBanks};
    });
    if (updatedBanks.indexOf(index) === -1) setUpdatedBanks([...updatedBanks, index]);
    if (key === 'unit_price') setBanksValueUpdated(true);
  };

  // Helper function to get default credit card fee
  const getDefaultCreditCardFee = useCallback(() => {
    return (!globalCreditCardFee.loading && globalCreditCardFee.data && globalCreditCardFee.data.globalCreditCardFee && globalCreditCardFee.data.globalCreditCardFee.fee)
      ? globalCreditCardFee.data.globalCreditCardFee.fee
      : '0';
  }, [globalCreditCardFee.loading, globalCreditCardFee.data]);

  // Handler for credit card fee toggle
  const handleCreditCardFeeToggle = useCallback(() => {
    const newState = !creditCardFeeEnabled;
    setCreditCardFeeEnabled(newState);

    setFormState(prevState => {
      if (newState) {
        // Toggle ON: Create credit card fee
        return {
          ...prevState,
          credit_card_fee: {
            fee: getDefaultCreditCardFee(),
            type: WcvType.RESOURCE,
            type_id: props.resource.id
          }
        };
      } else {
        // Toggle OFF: Remove credit card fee
        return {
          ...prevState,
          credit_card_fee: undefined
        };
      }
    });
  }, [creditCardFeeEnabled, getDefaultCreditCardFee, props.resource.id]);

  //3. setup validation and error hook
  const validateSchema: any = {
    description: {
      presence: {allowEmpty: false, message: 'is required'},
    },
    internal_name: {
      presence: {allowEmpty: false, message: 'is required'},
      length: {
        maximum: 191
      },
    },
    display_name: {
      presence: {allowEmpty: false, message: 'is required'},
      length: {
        maximum: 500
      },
    },
  };

  useEffect(() => {
    if (formState.has_dates) {
      validateSchema.available_from = {presence: {allowEmpty: false, message: 'is required'},};
      validateSchema.available_to = {presence: {allowEmpty: false, message: 'is required'},};
    }

    if (formState.resource_type === ResourceType.FOLDER) {
      validateSchema.display_name = {presence: {allowEmpty: true, message: 'is required'},};
      delete validateSchema.description
    }

    if (formState.resource_type === ResourceType.ROOM) {
      validateSchema.guest_capacity = {presence: {allowEmpty: false, message: 'is required'},};
    }

    if (formState.resource_type === ResourceType.TOUR) {
      validateSchema.comms_email_address = {
        presence: {allowEmpty: false, message: 'is required'},
        email: true,
        length: {
          maximum: 64
        }
      };
    }

    const newErrors = validate(formState, validateSchema);
    const bankError = formState.resource_banks ? formState.resource_banks.reduce((value, resourceBank) => {
      if (value) return true;
      if (!(resourceBank.capacity > -1) || String(resourceBank.capacity) === '') return true;
      if (String(resourceBank.unit_price) === '') return true;
      if (!(resourceBank.warning_count > -1) || String(resourceBank.warning_count) === '') return true;
      return false;
    }, false) : false;

    setErrors(state => ({
      ...state,
      isValid: !newErrors && !bankError,
      errors: newErrors || {}
    }));
  }, [formState]);

  const banksChanged = (formState.has_dates !== props.resource.has_dates) ||
      (formState.consumable !== props.resource.consumable) ||
      (formState.available_from !== props.resource.available_from) ||
      (formState.available_to !== props.resource.available_to);

  let tileHeight = 80;
  if (formState.consumable) tileHeight += 150;
  if (formState.has_dates) tileHeight += 30;

  //4. Handle save form
  const onSaveClick = () => {
    if (!isNewResource && banksValueUpdated && hasActiveOptions) {
      setOpenUpdateOptions(true);
    } else {
      executeSave();
    }
  };

  const executeSave = (updateOptions: boolean = false) => {
    if (isNewResource) {
      const sanitisedData: CreateResourceVars = {
        data: {
          ...{
            ...formState,
            id: undefined,
            __typename: undefined,
            roomResources: undefined,
            tour_roles: undefined,
            payment_schedule: undefined,
            documents: undefined,
            available_from: formatGraphQlDate(formState.available_from),//formState.available_from ? moment(formState.available_from).format('YYYY-MM-DD') : undefined,
            available_to: formatGraphQlDate(formState.available_to),//formState.available_to ? moment(formState.available_to).format('YYYY-MM-DD') : undefined,
            booking_tool_edit_cutoff: formatGraphQlDate(formState.booking_tool_edit_cutoff),
            max_additional_nights: Number(formState.max_additional_nights),
            max_nights_before: Number(formState.max_nights_before),
            max_nights_after: Number(formState.max_nights_after),
            credit_card_fee: undefined,
          },
        }
      };

      if (formState.resource_type === ResourceType.FOLDER) {
        sanitisedData.data.display_name = sanitisedData.data.internal_name;
      }

      if (formState.resource_type !== ResourceType.ROOM) {
          sanitisedData.data.guest_capacity = undefined;
      }

      if (formState.tour_roles) {
        const createRoles = formState.tour_roles!
            .map(tourRole => ({
              tour_role: tourRole.tour_role,
              age_lower_bound: tourRole.age_lower_bound,
              age_upper_bound: tourRole.age_upper_bound,
            }));

        sanitisedData.data.tour_roles = {};
        if (createRoles.length > 0) sanitisedData.data.tour_roles.create = createRoles;
      }

      sanitisedData.data.credit_card_fee = {};
      if (formState.credit_card_fee) {
          sanitisedData.data.credit_card_fee.create = {
            fee: formState.credit_card_fee.fee.toString(),
            type: WcvType.RESOURCE,
            type_id: props.resource.id
          }
      }

      sendCreateResource({variables: sanitisedData})
          .then((response) => {
            enqueueSnackbar('Resource Saved.', {variant: 'success'});
            if (props.onResourceCreated) {
              props.onResourceCreated(response!.data!.createResource);
            } else {
              history.push('/resources');
            }
          })
          .catch(() => {
            enqueueSnackbar('Save Resource Failed!', {variant: 'error'});
          });
    } else {
      const sanitisedData: UpdateResourceVars = {
        data: {
          ...{
            ...formState,
            __typename: undefined,
            parent: undefined,
            resources: undefined,
            resource_banks: undefined,
            roomResources: undefined,
            options: undefined,
            tour_roles: undefined,
            payment_schedule: undefined,
            documents: undefined,
            credit_card_fee: undefined,
            available_from: formatGraphQlDate(formState.available_from),
            available_to: formatGraphQlDate(formState.available_to),
            booking_tool_edit_cutoff: formatGraphQlDate(formState.booking_tool_edit_cutoff),
            max_additional_nights: Number(formState.max_additional_nights),
            max_nights_before: Number(formState.max_nights_before),
            max_nights_after: Number(formState.max_nights_after),
          },
        }
      };

      if (formState.resource_type === ResourceType.FOLDER) {
        sanitisedData.data.display_name = sanitisedData.data.internal_name;
      }

      if (updatedBanks.length > 0) {
        sanitisedData.data.resource_banks = {
          update: updatedBanks.map(index => ({
            id: formState.resource_banks![index].id,
            capacity: String(formState.resource_banks![index].capacity) !== '' ? formState.resource_banks![index].capacity : 0,
            warning_count: String(formState.resource_banks![index].warning_count) !== '' ? formState.resource_banks![index].warning_count : 0,
            unit_price: String(formState.resource_banks![index].unit_price) !== '' ? formState.resource_banks![index].unit_price : 0,
          })),
        };
      }

      if (props.resource.tour_roles) {

        const deleteRoles = props.resource.tour_roles!
            .filter(tourRole => formState.tour_roles!.find(checkRole => checkRole.tour_role === tourRole.tour_role) === undefined)
            .map(tourRole => tourRole.id);
        const createRoles = formState.tour_roles!
            .filter(tourRole => props.resource.tour_roles!.find(checkRole => checkRole.tour_role === tourRole.tour_role) === undefined)
            .map(tourRole => ({
              tour_role: tourRole.tour_role,
              age_lower_bound: tourRole.age_lower_bound,
              age_upper_bound: tourRole.age_upper_bound,
            }));
        const updateRoles = formState.tour_roles!
            .filter(tourRole => createRoles.find(checkRole => checkRole.tour_role === tourRole.tour_role) === undefined)
            .map(tourRole => ({
              id: tourRole.id,
              tour_role: tourRole.tour_role,
              age_lower_bound: tourRole.age_lower_bound,
              age_upper_bound: tourRole.age_upper_bound,
            }));

        sanitisedData.data.tour_roles = {};
        if (createRoles.length > 0) sanitisedData.data.tour_roles.create = createRoles;
        if (updateRoles.length > 0) sanitisedData.data.tour_roles.update = updateRoles;
        if (deleteRoles.length > 0) sanitisedData.data.tour_roles.delete = deleteRoles;
      }

        sanitisedData.data.credit_card_fee = {};
        // Use formState.credit_card_fee as the source of truth for whether fee should exist
        if (formState.credit_card_fee) {
            if (props.resource.credit_card_fee && props.resource.credit_card_fee.id) {
                sanitisedData.data.credit_card_fee.update = {
                    id: props.resource.credit_card_fee.id,
                    fee: formState.credit_card_fee.fee.toString(),
                    type: WcvType.RESOURCE,
                    type_id: props.resource.id
                }
            } else {
                sanitisedData.data.credit_card_fee.create = {
                    fee: formState.credit_card_fee.fee.toString(),
                    type: WcvType.RESOURCE,
                    type_id: props.resource.id
                }
            }
        } else if (props.resource.credit_card_fee) {
            // formState.credit_card_fee is undefined/null, so delete existing fee
            sanitisedData.data.credit_card_fee.delete = props.resource.credit_card_fee.id
        }
        sendUpdateResource({variables: sanitisedData})
          .then((response) => {
            enqueueSnackbar('Resource Updated.', {variant: 'success'});

            // Update local state to reflect server response
            if (response.data && response.data.updateResource) {
              const updatedResource = response.data.updateResource;
              const serverCreditCardFee = updatedResource.credit_card_fee || undefined;
              setFormState(prevState => ({
                ...prevState,
                credit_card_fee: serverCreditCardFee
              }));
              setCreditCardFeeEnabled(!!serverCreditCardFee);
            }

            if (updateOptions) {
              sendUpdateResourceOptionCosts({variables: {id: props.resource.id}})
                  .then(() => {
                    enqueueSnackbar('Option Values Updated.', {variant: 'success'});
                  })
                  .catch(() => {
                    enqueueSnackbar('Option Values Udate Failed!', {variant: 'error'});
                  });
            }
          })
          .catch(() => {
              enqueueSnackbar('Resource Update Failed!', {variant: 'error'});
          });
    }
  };

  const handleFixRoomResourceCapacity = () => {
    sendFixRoomResourceCapacity({variables: {id: props.resource.id}})
        .then(() => {
          enqueueSnackbar('Fix Room Resource Capacity Done', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Fix Room Resource Capacity Failed!', {variant: 'error'});
        });
  };

  const handleLock = () => {
    if(canLock) {
      sendUpdateResource({variables: {data: {id: formState.id, locked: !formState.locked}}})
          .then((response) => {
            enqueueSnackbar(response.data!.updateResource.locked ? 'Resource Locked' : 'Resource Unlocked.', {variant: 'success'});
          })
          .catch(() => {
            enqueueSnackbar('Resource Update Failed!', {variant: 'error'});
          });
    } else {
      enqueueSnackbar('You do not have permission to lock this Resource.', {variant: 'error'});
    }
  };

  const handleClickOpenDelete = () => {
    setOpenDelete(true);
  };

  const handleDelete = () => {
    sendDeleteResource({variables: {id: formState.id}})
        .then(() => {
          enqueueSnackbar('Resource Deleted.', {variant: 'success'});
          if (props.onResourceDeleted) {
            props.onResourceDeleted(formState.id);
          } else {
            history.push('/resources');
          }
        })
        .catch(() => {
          enqueueSnackbar('Deleted Resource Failed!', {variant: 'error'});
        });

    setOpenDelete(false);
  };

  const handleUpdateOptions = (updateOptions: boolean) => {
    if (!updateOptions) {
      executeSave();
    } else {
      executeSave(true)
    }
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleCloseUpdateOptions = () => {
    setOpenUpdateOptions(false);
  };

  const onCloneClick = () => {
    sendCloneResource({variables: {id: props.resource.id!, deepClone: false}})
        .then((response) => {
          enqueueSnackbar('Resource Cloned.', {variant: 'success'});
          const newResource = response.data!.cloneResource as Resource;
          if (props.onResourceCreated) {
            props.onResourceCreated(newResource);
          } else {
            history.push('/resources/' + newResource.id);
          }
        })
        .catch(() => {
          enqueueSnackbar('Resource Clone Failed!', {variant: 'error'});
        });
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>, attributeName: string) => {
    if (event.target.files) {
      const sanitisedData: UploadFileVars = {
        data: {
          file: event.target.files[0],
          type: WcvType.RESOURCE,
          type_id: formState.id,
          attribute: attributeName,
        },
      };

      sendUploadFile({variables: sanitisedData})
          .then((response) => {
            enqueueSnackbar('File Upload Success!', {variant: 'success'});
            setFormState({...formState, [attributeName]: response.data!.uploadFile});
          })
          .catch(() => {
            enqueueSnackbar('File Upload Failed!', {variant: 'error'});
          });
    }
  };

  const [openContactModal, setOpenContactModal] = React.useState({message: '', open: false});
  const handleContactModalOpen = () => setOpenContactModal({
    message: ``,
    open: true
  });
  const handleContactModalClose = () => setOpenContactModal({message: '', open: false});

  const [openPaymentScheduleModal, setOpenPaymentScheduleModal] = React.useState(false);
  const handlePaymentScheduleModalOpen = () => setOpenPaymentScheduleModal(true);
  const handlePaymentScheduleModalClose = () => setOpenPaymentScheduleModal(false);

  const [openChangeParentModal, setOpenChangeParentModal] = React.useState(false);
  const handleChangeParentModalOpen = () => setOpenChangeParentModal(true);
  const handleChangeParentModalClose = () => setOpenChangeParentModal(false);

  const canLockResource = (formState.resource_type !== ResourceType.TOUR || (formState.resource_type === ResourceType.TOUR && isSuperAdmin));
  const canEdit = !formState.locked && canLock && isSuperAdmin;

  function roomsUsed(index: number) {
      // get our original capacities
      const capacities = props && props.resource.resources && props.resource.resource_banks && props.resource.resource_banks.map(resourceBank => resourceBank.capacity);
      // get remaining
      const remaining = props && props.resource.resources && props.resource.resource_banks && props.resource.resource_banks.map(resourceBank => resourceBank.remaining);
      // @ts-ignore
      return capacities[index] - remaining[index]
  }

  const isSaveButtonDisabled = !errors.isValid || !canEdit;

  return (
      <Fragment>
        <LoadingOverlay loading={loadingData.loading}>
          <Card className={classes.container}>
            <CardHeader className={classes.resourceCardHeader} classes={{action: classes.buttonWrapper}} title={'Resource Details'} action={[
                isSuperAdmin && props.resource && <Button key={0} variant="outlined" onClick={() => handleChangeParentModalOpen()}>Change Parent</Button>,
              formState.resource_type === ResourceType.TOUR &&
              <Button key={1} variant="outlined" onClick={() => handleContactModalOpen()}>Email Tour</Button>,
              formState.resource_type === ResourceType.TOUR &&
              <Button key={2} variant="outlined" onClick={() => handlePaymentScheduleModalOpen()}>Payment
                Schedule</Button>,
              !isNewResource && (
                  <IconButton key={3}
                              color="inherit"
                              onClick={handleLock}
                              disabled={!canLockResource}
                  >
                    {formState.locked && (<LockClosedIcon/>)}
                    {!formState.locked && (<LockOpenIcon/>)}
                  </IconButton>
              )]}/>
            <CardContent className={classes.card}>
              <Grid container spacing={3}>

                <Grid item xs={12}>
                  <TextField
                      {...hasError('internal_name')}
                      fullWidth
                      label="Internal Name"
                      name="internal_name"
                      onChange={handleFieldChange}
                      value={formState.internal_name}
                      variant="outlined"
                      disabled={!canEdit}
                  />
              </Grid>

              <Grid item xs={12}>
                <TextField
                    {...hasError('resource_type')}
                    fullWidth
                    label="Resource Type"
                    name="resource_type"
                    onChange={handleFieldChange}
                    select
                    // eslint-disable-next-line react/jsx-sort-props
                    SelectProps={{native: true}}
                    value={formState.resource_type}
                    variant="outlined"
                    disabled={!canEdit}
                >
                  {ResourceTypes.map((type, i) => type && (
                      <option key={type.name} value={i}>
                        {type.name}
                      </option>
                  ))}
                </TextField>
              </Grid>


                {formState.resource_type === ResourceType.ROOM && (
                    <Grid item xs={12}>
                      <TextField
                          {...hasError('guest_capacity')}
                          fullWidth
                          type="number"
                          label="Guest Capacity"
                          name="guest_capacity"
                          onChange={handleFieldChange}
                          value={formState.guest_capacity}
                          InputProps={{inputProps: {min: 0,}}}
                          variant="outlined"
                          disabled={!canEdit}
                      />
                    </Grid>
                )}

                {formState.resource_type !== ResourceType.FOLDER && (
                    <React.Fragment>
                      <Grid item xs={12}>
                        <TextField
                            {...hasError('display_name')}
                            fullWidth
                            label="Display Name"
                            name="display_name"
                            onChange={handleFieldChange}
                            value={formState.display_name}
                            variant="outlined"
                            disabled={!canEdit}
                        />
                      </Grid>

                      <Grid item md={12} xs={12}>
                        <TextField
                            {...hasError('description')}
                            fullWidth
                            label="Description"
                            name="description"
                            onChange={handleFieldChange}
                            value={formState.description}
                            variant="outlined"
                            disabled={!canEdit}
                        />
                      </Grid>

                      {formState.resource_type === ResourceType.TOUR && (
                          <Grid item xs={12}>
                            <TextField
                                {...hasError('invoice_document_id')}
                                fullWidth
                                label="Invoice Document"
                                name="invoice_document_id"
                                onChange={handleFieldChange}
                                select
                                // eslint-disable-next-line react/jsx-sort-props
                                SelectProps={{native: true}}
                                InputLabelProps={{shrink: true}}
                                value={formState.invoice_document_id}
                                variant="outlined"
                                disabled={!canEdit}
                            >
                              <option value={-1}>Use WCV Default</option>
                              {props.resource.documents && props.resource.documents.map((document) => (
                                  <option key={'document_' + document.id} value={document.id}>
                                    {document.title}
                                  </option>
                              ))}
                            </TextField>
                            <Link target="_blank"  href={getHost() + '/invoice_template/' + WcvType.RESOURCE + '/' + props.resource.id}>
                              <Button variant="contained"><T phrase="Download Template"/></Button>
                            </Link>
                          </Grid>
                      )}
                      {formState.resource_type === ResourceType.TOUR && (
                          <Grid item xs={12}>
                            <TextField
                                {...hasError('netsuite_tour_type')}
                                fullWidth
                                label="Netsuite Tour Type"
                                name="netsuite_tour_type"
                                onChange={handleFieldChange}
                                value={formState.netsuite_tour_type}
                                variant="outlined"
                                disabled={!canEdit}
                            />
                          </Grid>
                      )}

                      {formState.resource_type === ResourceType.TOUR && (
                        <Grid item md={12}>
                          <WCVDatePicker
                              {...hasError('booking_tool_edit_cutoff')}
                              id="booking_tool_edit_cutoff"
                              label="Booking Tool Edit Cutoff Date:"
                              value={formState.booking_tool_edit_cutoff}
                              onChange={(date, value) => {
                                setFormState({
                                  ...formState,
                                  booking_tool_edit_cutoff: value ? value : undefined
                                })
                              }}
                              disabled={!canEdit}
                          />
                        </Grid>
                      )}

                      {formState.resource_type === ResourceType.TOUR && (
                          <Grid item xs={12}>
                            <Typography variant="h5">Available Tour Roles:</Typography>
                            {TourRoleTypes.map((role, i) => {
                              const tourRole = formState.tour_roles && formState.tour_roles!.find(checkRole => checkRole.tour_role === i);
                              return (
                                  <Grid key={i} component="label" container alignItems="center" spacing={1}>
                                    <Grid item xs={2}>
                                      <Switch
                                          checked={tourRole !== undefined}
                                          color="secondary"
                                          edge="start"
                                          name={'tour_role_' + i}
                                          onChange={(event) => {
                                            let newRoles = [];
                                            if (event.target.checked) {
                                              //add
                                              const newRole = tourRole ? {...tourRole} : {
                                                tour_role: i,
                                                tour_resource_id: formState.id,
                                                id: -1,
                                                age_lower_bound: 3,
                                                age_upper_bound: 150,
                                              }
                                              newRoles = [...formState.tour_roles!, newRole];
                                            } else {
                                              //remove
                                              newRoles = formState.tour_roles!.filter(checkRole => checkRole.tour_role !== i);
                                            }
                                            setFormState({...formState, tour_roles: newRoles});
                                          }}
                                          disabled={!canEdit}
                                      />
                                    </Grid>
                                    <Grid item xs={4}><Typography variant="h5">{role.name}</Typography></Grid>
                                    {tourRole && (
                                        <Grid item xs={3}>
                                          <TextField
                                              type="number"
                                              fullWidth
                                              label="Min Age"
                                              name="age_lower_bound"
                                              onChange={(event) => {
                                                const newRole = {
                                                  ...tourRole,
                                                  age_lower_bound: Number(event.target.value)
                                                };
                                                const newRoles = [...formState.tour_roles!.filter(checkRole => checkRole.tour_role !== i), newRole];
                                                setFormState({...formState, tour_roles: newRoles});
                                              }}
                                              InputProps={{inputProps: {min: 0,}}}
                                              value={tourRole.age_lower_bound}
                                              disabled={!canEdit}
                                              margin={'dense'}
                                          />
                                        </Grid>
                                    )}
                                    {tourRole && (
                                        <Grid item xs={3}>
                                          <TextField
                                              type="number"
                                              fullWidth
                                              label="Max Age"
                                              name="age_upper_bound"
                                              onChange={(event) => {
                                                const newRole = {
                                                  ...tourRole,
                                                  age_upper_bound: Number(event.target.value)
                                                };
                                                const newRoles = [...formState.tour_roles!.filter(checkRole => checkRole.tour_role !== i), newRole];
                                                setFormState({...formState, tour_roles: newRoles});
                                              }}
                                              InputProps={{inputProps: {min: 0,}}}
                                              value={tourRole.age_upper_bound}
                                              margin={'dense'}
                                              disabled={!canEdit}
                                          />
                                        </Grid>
                                    )}
                                  </Grid>
                              )
                            })}

                            {formState.payment_schedule &&  openPaymentScheduleModal && (
                                <PaymentScheduleDetailsModal onClose={handlePaymentScheduleModalClose}
                                                             open={openPaymentScheduleModal}
                                                             paymentScheduleId={formState.payment_schedule.id}/>
                            )}
                          </Grid>
                      )}

                      {formState.resource_type === ResourceType.TOUR && (
                          <React.Fragment>
                            <Grid item xs={12}>
                              <TextField
                                  fullWidth
                                  label="Comms Email Address"
                                  name="comms_email_address"
                                  {...hasError('comms_email_address')}
                                  onChange={handleFieldChange}
                                  select
                                  // eslint-disable-next-line react/jsx-sort-props
                                  SelectProps={{native: true}}
                                  InputLabelProps={{shrink: true}}
                                  value={String(formState.comms_email_address)}
                                  variant="outlined"
                              >
                                <option value="">please select...</option>
                                {emails.map((email, i) => (
                                    <option key={i} value={email.email}>
                                      {email.email}
                                    </option>
                                ))}
                              </TextField>

                            </Grid>

                            <Grid item xs={12}>
                              <MediumEditorField
                                  {...hasError('rain_date_note')}
                                  fullWidth
                                  label="Rain Date Note"
                                  variant="outlined"
                                  name="rain_date_note"
                                  multiline
                                  value={formState.rain_date_note}
                                  onChange={(event: any) => setFormState({
                                    ...formState,
                                    rain_date_note: event.target.value
                                  })}
                                  disabled={!canEdit}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <MediumEditorField
                                  {...hasError('cancellation_policy')}
                                  fullWidth
                                  label="Cancellation Policy"
                                  variant="outlined"
                                  name="cancellation_policy"
                                  multiline
                                  value={formState.cancellation_policy}
                                  onChange={(event: any) => setFormState({
                                    ...formState,
                                    cancellation_policy: event.target.value
                                  })}
                                  disabled={!canEdit}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <MediumEditorField
                                  {...hasError('terms_and_conditions')}
                                  fullWidth
                                  label="Terms & Conditions"
                                  variant="outlined"
                                  name="terms_and_conditions"
                                  multiline
                                  value={formState.terms_and_conditions}
                                  onChange={(event: any) => setFormState({
                                    ...formState,
                                    terms_and_conditions: event.target.value
                                  })}
                                  disabled={!canEdit}
                              />
                            </Grid>
                          </React.Fragment>
                      )}

                      {formState.resource_type !== ResourceType.SEASON && (
                          <React.Fragment>
                            <Grid item md={12} xs={12}>
                              <Grid component="label" container alignItems="center" spacing={1}>
                                <Grid item>
                                  <Switch
                                      checked={formState.consumable}
                                      color="secondary"
                                      edge="start"
                                      name="consumable"
                                      onChange={handleFieldChange}
                                      value={formState.consumable}
                                      disabled={!canEdit}
                                  />
                                </Grid>
                                <Grid item><Typography variant="h5">Is Consumable?</Typography></Grid>
                              </Grid>
                            </Grid>

                            {formState.consumable && (
                                <Grid item md={12} xs={12}>
                                  <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                      <Switch
                                          checked={formState.allow_multiple}
                                          color="secondary"
                                          edge="start"
                                          name="allow_multiple"
                                          onChange={handleFieldChange}
                                          value={formState.allow_multiple}
                                          disabled={!canEdit}
                                      />
                                    </Grid>
                                    <Grid item><Typography variant="h5">Allow Multiple?</Typography></Grid>
                                  </Grid>
                                </Grid>
                            )}

                            <Grid item md={12} xs={12}>
                              <Grid component="label" container alignItems="center" spacing={1}>
                                <Grid item>
                                  <Switch
                                      checked={formState.has_price}
                                      color="secondary"
                                      edge="start"
                                      name="has_price"
                                      onChange={handleFieldChange}
                                      value={formState.has_price}
                                      disabled={!canEdit}
                                  />
                                </Grid>
                                <Grid item><Typography variant="h5">Has Price?</Typography></Grid>
                              </Grid>
                            </Grid>

                            <Grid item md={12} xs={12}>
                              <Grid component="label" container alignItems="center" spacing={1}>
                                <Grid item>
                                  <Switch
                                      checked={formState.has_dates}
                                      color="secondary"
                                      edge="start"
                                      name="has_dates"
                                      onChange={handleFieldChange}
                                      value={formState.has_dates}
                                      disabled={!canEdit}
                                  />
                                </Grid>
                                <Grid item><Typography variant="h5">Has Dates?</Typography></Grid>
                              </Grid>
                            </Grid>

                            {formState.resource_type === ResourceType.HOTEL && (
                                <Grid item md={12} xs={12}>
                                  <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                      <Switch
                                checked={formState.use_room_bank}
                                color="secondary"
                                edge="start"
                                name="use_room_bank"
                                onChange={handleFieldChange}
                                value={formState.use_room_bank}
                                disabled={!canEdit}
                              />
                            </Grid>
                            <Grid item><Typography variant="h5">Use Room Bank?</Typography></Grid>
                          </Grid>
                        </Grid>
                      )}

                      {formState.resource_type === ResourceType.HOTEL && formState.use_room_bank && (
                        <Grid item xs={12} >
                          <Grid container spacing={1}>
                            <Grid item xs={formState.room_bank_resource_id? 10 : 12}>
                              <TextField
                                {...hasError('room_bank_resource_id')}
                                fullWidth
                                label="Room Bank"
                                name="room_bank_resource_id"
                                onChange={handleFieldChange}
                                select
                                // eslint-disable-next-line react/jsx-sort-props
                                SelectProps={{native: true}}
                                InputLabelProps={{shrink: true}}
                                value={formState.room_bank_resource_id}
                                variant="outlined"
                                disabled={!canEdit}
                              >
                                <option value={-1}>Choose Room Bank</option>
                                {roomBankData.data && roomBankData.data.availableRoomBanks && roomBankData.data.availableRoomBanks!.map((roomBank) => (
                                  <option key={'room_bank_'+roomBank.id} value={roomBank.id}>
                                    {roomBank.internal_name}
                                  </option>
                                ))}
                              </TextField>
                            </Grid>
                            {formState.room_bank_resource_id && (
                            <Grid item xs={2}>
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    if(props.onResourceView) {
                                      props.onResourceView({
                                        __typename: 'Resource',
                                        id: formState.room_bank_resource_id!,
                                        internal_name: 'room_bank',
                                        display_name: 'Room Bank',
                                        t_and_c_url: '',
                                      });
                                    } else {
                                      history.push('/resources/' + formState.room_bank_resource_id);
                                    }
                                  }}
                                >
                                  View
                                </Button>
                            </Grid>
                            )}
                          </Grid>

                        </Grid>
                      )}

                      {formState.has_dates && (
                        <Grid item xs={12}>
                          <Grid container justify="space-around" className={classes.dateWrapper}>
                            <WCVDatePicker
                                {...hasError('available_from')}
                                id="available_from"
                                label="Available From:"
                                value={formState.available_from}
                                onChange={(date, value) => {
                                  setFormState({
                                    ...formState,
                                    available_from: value ? value : undefined
                                  })
                                }}
                                style={{marginRight: theme.spacing(1)}}
                                disabled={!canEdit}
                            />
                            <WCVDatePicker
                                {...hasError('available_to')}
                                id="available_to"
                                label="Available To:"
                                value={formState.available_to}
                                onChange={(date, value) => {
                                  setFormState({
                                    ...formState,
                                    available_to: value ? value : undefined
                                  })
                                }}
                                disabled={!canEdit}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {isNewResource && (
                        <Grid item md={12} xs={12} className={classes.gridListContainer}>
                          <Typography variant="h5" style={{width: '100%'}}>Resource Banks</Typography>
                          <Typography variant="h6" color="error" style={{paddingBottom: theme.spacing(3)}}>
                            Resource needs to be saved to update available resource banks.
                          </Typography>
                        </Grid>
                      )}

                      {formState.resource_banks && (formState.consumable || formState.has_price) && formState.resource_banks.length > 0 && (
                        <Grid item md={12} xs={12} className={classes.gridListContainer}>
                          <Grid container alignItems="center" style={{width: '100%', marginBottom: 8}}>
                            <Grid item>
                              <Typography variant="h5">Resource Banks</Typography>
                            </Grid>
                            {formState.consumable && props.resource && props.resource.id !== -1 && (
                              <Grid item style={{marginLeft: 16}}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="primary"
                                  component="a"
                                  href={getHost() + '/room_totals_xlsx/' + props.resource.id}
                                  target="_blank"
                                >
                                  Download Room Totals Report
                                </Button>
                              </Grid>
                            )}
                          </Grid>

                          {banksChanged && (
                            <Typography variant="h6" color="error" style={{paddingBottom: theme.spacing(3)}}>
                              Resource changes need to be saved to update available resource banks.
                            </Typography>
                          )}

                          <Grid container spacing={0}>

                            <Grid item md={12}>
                              <GridList className={classes.gridList} cols={4.5} style={{overflow: 'auto'}}>
                                {formState.resource_banks.map((resourceBank, i) => (
                                    <GridListTile key={resourceBank.id} style={{height: 'auto'}}>
                                      {formState.has_dates && (
                                          <Typography variant="body2">{formatDate(resourceBank.date)}</Typography>
                                      )}
                                      {formState.consumable && (
                                          <TextField
                                              fullWidth
                                              error={!(resourceBank.capacity > -1)
                                                  || String(resourceBank.capacity) === ''
                                              }
                                              label="capacity"
                                              name="capacity"
                                              type="number"
                                              InputProps={{inputProps: {min: 0,}}}
                                              onChange={event => handleBankChange('capacity', event.target.value, i)}
                                              value={resourceBank.capacity}
                                              margin="dense"
                                              disabled={banksChanged || !canEdit}
                                          />
                                      )}
                                      {formState.consumable && (
                                          <TextField
                                              fullWidth
                                              label="remaining"
                                              type="number"
                                              value={resourceBank.remaining}
                                              margin="dense"
                                              disabled={true}
                                          />
                                      )}
                                        {formState.consumable && (
                                            <TextField
                                                fullWidth
                                                label="in house"
                                                type="number"
                                                value={roomsUsed(i)}
                                                margin="dense"
                                                disabled={true}
                                            />
                                        )}
                                      {formState.consumable && (
                                          <TextField
                                              fullWidth
                                              error={!(resourceBank.warning_count > -1) || String(resourceBank.warning_count) === ''}
                                              label="warning count"
                                              name="warning_count"
                                              type="number"
                                              onChange={event => handleBankChange('warning_count', event.target.value, i)}
                                              value={resourceBank.warning_count}
                                              margin="dense"
                                              disabled={banksChanged || !canEdit}
                                          />
                                      )}
                                      {formState.has_price && (
                                          <TextField
                                              fullWidth
                                              error={!(resourceBank.unit_price > -1) || String(resourceBank.unit_price) === ''}
                                              label="price"
                                              name="unit_price"
                                              type="number"
                                              onChange={event => handleBankChange('unit_price', event.target.value, i)}
                                              value={resourceBank.unit_price}
                                              margin="dense"
                                              disabled={banksChanged || !canEdit}
                                          />
                                      )}
                                    </GridListTile>
                                ))}
                              </GridList>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}


                      {formState.resource_type === ResourceType.TOUR && (
                        <React.Fragment>
                          <Grid item sm={4} xs={12}>
                            <TextField
                              {...hasError('max_additional_nights')}
                              fullWidth
                              type="number"
                              InputProps={{inputProps: {min: 0,}}}
                              label="Maximum Additional Nights"
                              name="max_additional_nights"
                              onChange={handleFieldChange}
                              value={formState.max_additional_nights}
                              variant="outlined"
                              disabled={!canEdit}
                            />
                          </Grid>
                          <Grid item sm={4} xs={12}>
                            <TextField
                              {...hasError('max_nights_before')}
                              fullWidth
                              type="number"
                              InputProps={{inputProps: {min: 0,}}}
                              label="# Available Before Event"
                              name="max_nights_before"
                              onChange={handleFieldChange}
                              value={formState.max_nights_before}
                              variant="outlined"
                              disabled={!canEdit}
                            />
                          </Grid>
                          <Grid item sm={4} xs={12}>
                            <TextField
                              {...hasError('max_nights_after')}
                              fullWidth
                              type="number"
                              InputProps={{inputProps: {min: 0,}}}
                              label="# Available After Event"
                              name="max_nights_after"
                              onChange={handleFieldChange}
                              value={formState.max_nights_after}
                              variant="outlined"
                              disabled={!canEdit}
                            />
                          </Grid>
                          {creditCardFeeEnabled &&
                            <Grid item sm={4} xs={12}>
                              <TextField
                                {...hasError('credit_card_fee')}
                                fullWidth
                                type="number"
                                InputProps={{
                                  inputProps: {min: 0},
                                  startAdornment: <InputAdornment position="start">%</InputAdornment>,
                                }}
                                label="Credit Card Fee"
                                name="credit_card_fee"
                                onChange={(event: any) => setFormState({
                                    ...formState,
                                    credit_card_fee: {
                                        fee: Number(event.target.value) < 0 ? '0' : event.target.value,
                                        type: WcvType.RESOURCE,
                                        type_id: props.resource.id
                                    }
                                })}
                                value={formState.credit_card_fee && formState.credit_card_fee.fee}
                                variant="outlined"
                                disabled={!canEdit}
                              />
                            </Grid>
                          }
                        </React.Fragment>
                      )}

                      {formState.consumable && (
                        <Grid item md={12} xs={12}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                            <Grid item>
                              <Switch
                                checked={formState.allow_manual_overbooking}
                                color="secondary"
                                edge="start"
                                name="allow_manual_overbooking"
                                onChange={handleFieldChange}
                                value={formState.allow_manual_overbooking}
                                disabled={!canEdit}
                              />
                            </Grid>
                            <Grid item><Typography variant="h5">Allow Manual Overbooking?</Typography></Grid>
                          </Grid>
                        </Grid>
                      )}

                      {formState.has_price && (
                        <Grid item md={12} xs={12}>
                          <Grid component="label" container alignItems="center" spacing={1}>
                            <Grid item>
                              <Switch
                                checked={formState.show_on_invoice}
                                color="secondary"
                                edge="start"
                                name="show_on_invoice"
                                onChange={handleFieldChange}
                                value={formState.show_on_invoice}
                                disabled={!canEdit}
                              />
                            </Grid>
                            <Grid item><Typography variant="h5">Show on Invoice?</Typography></Grid>
                          </Grid>
                        </Grid>
                      )}

                      <Grid item md={12} xs={12}>
                        <Grid component="label" container alignItems="center" spacing={1}>
                          <Grid item>
                            <Switch
                              color="secondary"
                              edge="start"
                              name="credit_card_fee_enabled"
                              onChange={handleCreditCardFeeToggle}
                              checked={creditCardFeeEnabled}
                              disabled={!canEdit}
                            />
                          </Grid>
                          <Grid item><Typography variant="h5">Credit Card Fee?</Typography></Grid>
                        </Grid>
                      </Grid>

                      <Grid item md={12} xs={12}>
                        <Grid component="label" container alignItems="center" spacing={1}>
                          <Grid item>
                            <Switch
                              checked={formState.disabled}
                              color="secondary"
                              edge="start"
                              name="disabled"
                              onChange={handleFieldChange}
                              value={formState.disabled}
                              disabled={!canEdit}
                            />
                          </Grid>
                          <Grid item><Typography variant="h5">Disabled?</Typography></Grid>
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  )}

                </React.Fragment>
              )}

              <Grid item xs={12} >
                  <Button
                      className={classes.saveButton}
                      onClick={onSaveClick}
                      variant="contained"
                      disabled={!errors.isValid || !canEdit || isSaveButtonDisabled}>
                      Save
                  </Button>

                <Can permission="resource.delete">
                  <Button onClick={handleClickOpenDelete} className={classes.deleteButton} variant="contained"
                          disabled={isNewResource || !canEdit}>
                    Delete
                  </Button>
                </Can>

                <Button className={classes.cloneButton} onClick={onCloneClick} variant="contained"
                        disabled={isNewResource || !errors.isValid || !canEdit}>
                  Clone
                </Button>
              </Grid>
              </Grid>
            </CardContent>
          </Card>


          {!isNewResource && formState.resource_type === ResourceType.TOUR && (
              <Card className={classes.container}>
                <CardHeader title="Tour Images"/>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      {formState.image_banner_url && (
                          <img src={getHost() + formState.image_banner_url} style={{width: 160, height: 100}}
                               alt="tour banner"/>)}
                      {!formState.image_banner_url && ('No Banner Image.')}
                      <br/>
                      <Typography variant="caption">Image should be 640px x 400px</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <input
                          accept="image/*"
                          style={{display: 'none'}}
                          id="image_banner_url"
                          multiple
                          type="file"
                          onChange={event => handleUpload(event, 'image_banner_url')}
                          value={''}
                      />
                      <label htmlFor="image_banner_url">
                        <Button variant="contained" component="span" className={classes.saveButton} disabled={!canEdit}>
                          Upload Banner
                        </Button>
                      </label>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
          )}

          {!isNewResource && formState.resource_type === ResourceType.TOUR && (
              <Card className={classes.container}>
                <CardHeader title="Documents" action={[
                  <Button key={1} color="primary" size="small" variant="outlined" onClick={() => {
                    const doc = getBlankDocument(meData.data!.me!.id);
                    doc.type = WcvType.RESOURCE;
                    doc.type_id = props.resource.id;
                    setSelectedDocument(doc);
                  }} disabled={!canEdit}>Add Document</Button>,
                  // <Link key={4}
                  //       href={getHost() + '/docs/' + btoa(JSON.stringify({'tour': props.resource.id}))}>
                  //   <Button variant="contained">Member Docs Zip</Button>
                  // </Link>,
                ]}/>
                <CardContent>
                  {selectedDocument && (
                      <DocumentDetailsContent document={selectedDocument} setSelectedDocument={setSelectedDocument}
                                              fileTypes=".php,application/pdf,image/*"/>
                  )}
                  {!selectedDocument && (
                      <DocumentListContent type={WcvType.RESOURCE} type_id={props.resource.id}/>
                  )}

                </CardContent>
              </Card>
          )}

          {!isNewResource && (
              <Card className={classes.container}>
                <CardHeader title="Child Resources" action={
                  <React.Fragment>
                    <Button
                        className={classes.saveButton}
                        onClick={() => {
                          const blankResourceOption = getBlankResourceOption(formState.id!);
                          if (props.onResourceOptionView) props.onResourceOptionView(blankResourceOption);
                        }}
                        variant="contained"
                        disabled={formState.locked || !canEdit}
                    >
                      + Option
                    </Button>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        disabled={isNewResource || formState.locked  || !canEdit}
                  onClick={() => {
                    if(props.onResourceView) {
                      props.onResourceView(getBlankResource(props.resource.id));
                    } else {
                      history.push('/resources/'+props.resource.id+'/new');
                    }
                  }}
                >
                  + Resource
                </Button>
              </React.Fragment>
            }/>
            <Divider/>
            <CardContent >
              {resourceData.loading && (<Typography>Loading...</Typography>)}
              {!resourceData.loading && (
                <ResourcesTree
                  resources={[props.resource]}
                  hideViewIds={[props.resource.id]}
                  onResourceView={props.onResourceView? props.onResourceView : (resource: Resource) => {
                    history.push('/resources/' + resource.id);
                  }}
                  onResourceOptionClick={props.onResourceOptionView? props.onResourceOptionView : () => {
                    console.log('resource option clicked with no handler.');
                  }}
                />
              )}
            </CardContent>
          </Card>
        )}

        {props.resource.parent && (
          <Card className={classes.container} >
            <CardHeader title="Parent Resource"/>
            <Divider/>
            <CardActions>
              <Button
                className={classes.saveButton}
                variant="contained"
                disabled={isNewResource}
                onClick={() => {
                  if(props.onResourceView) {
                    props.onResourceView(props.resource.parent!);
                  } else {
                    history.push('/resources/' + props.resource.parent!.id);
                  }
                }}
              >
                Show Parent
              </Button>
            </CardActions>
          </Card>
        )}

          {openDelete && (
              <ConfirmDelete onClose={handleCloseDelete} onDeleteClick={handleDelete} open={true}/>
          )}
          {openUpdateOptions && (
              <ConfirmModal
                  onClose={handleCloseUpdateOptions}
                  cancel={'Dont Update Options'}
                  onCancelClick={() => handleUpdateOptions(false)}
                  confirm={'Update Options'}
                  onConfirmClick={() => handleUpdateOptions(true)}
                  open={true}
                  title={'Resource Value Updated!'}
                  message={'There are users that have this option allocated on a previous price. Would you like to update those prices to the new price?'}
              />
          )}
          {openChangeParentModal && (
              <ChangeResourceParentModal onClose={handleChangeParentModalClose} resource={props.resource} open={true}/>
          )}

          {formState.id !== -1 && openContactModal.open && (
              <SendEmailToGroup onClose={handleContactModalClose} open={openContactModal.open} tourId={formState.id}
                                fromEmail={formState.comms_email_address}
                                heading={'Send Message to Tour'}
                                message={'This message will be sent to every billing party paying member in the groups on this Tour.'}
                                individual={true}
              />
          )}
        </LoadingOverlay>
    </Fragment>
  );
};

const ResourceEdit = (props: {
  resourceId: number,
  parentId?: number,
  onResourceView?: (resource: Resource) => void,
  onResourceBankView?: (resourceBank: ResourceBank) => void,
  onResourceOptionView?: (resourceOption: ResourceOption) => void,
  onResourceCreated?: (resource: Resource) => void,
  onResourceDeleted?: (resourceId: number) => void,
}) => {
  const {history} = useReactRouter();
  const { resourceId, parentId, ...rest } = props;
  const newResource = resourceId === -1;
  const resourceData = getResource(resourceId);
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    if (!newResource && resourceData.error) {
      enqueueSnackbar(`There was an error loading Resource ${resourceId}`, {variant: 'error'});
      history.push('/resources');
    }
  }, [resourceData.error]);

  return (
    <LoadingOverlay loading={!newResource && resourceData.loading}>
      {!newResource && !resourceData.loading && resourceData.data && resourceData.data.resource && (
        <ResourceEditPure resource={resourceData.data.resource} {...rest} />
      )}
      {newResource && (
        <ResourceEditPure resource={getBlankResource(parentId)} {...rest} />
      )}
    </LoadingOverlay>
  );
};

export default ResourceEdit;
